import type { ItemStubsResponseDTO } from '@tempo-io/roadmap-dto';
import type { Item } from '@/common/api/types/items';
import type { DependencyForecastDates } from '@/features/item-panel/components/hooks/useDependencyForecastDates';
import type {
  DependencySectionValue,
  ItemWithSchedule,
} from '@/features/item-panel/components/tabs/dependencies/lib/types';

export const toItemStubs = (rows: ItemWithSchedule[]): ItemStubsResponseDTO =>
  rows.map((row) => ({ id: row.id, matched: true }));

export const withSchedules = (
  items: Item[],
  forecastDates: Record<number, DependencyForecastDates>,
): ItemWithSchedule[] =>
  items.map((dep) => ({
    ...dep,
    forecastedStartDateTime: forecastDates[dep.id]?.forecastedStartDateTime ?? null,
    forecastedFinishDateTime: forecastDates[dep.id]?.forecastedFinishDateTime ?? null,
  }));

const PLANNED_DATE_FIELD_BY_DATE_FIELD = {
  forecastedStartDateTime: 'plannedStartDateTime',
  forecastedFinishDateTime: 'plannedFinishDateTime',
  plannedStartDateTime: 'plannedStartDateTime',
  plannedFinishDateTime: 'plannedFinishDateTime',
} as const;

export const sortByDate = (
  items: ItemWithSchedule[],
  dateField:
    | 'forecastedStartDateTime'
    | 'forecastedFinishDateTime'
    | 'plannedStartDateTime'
    | 'plannedFinishDateTime',
  sortDirection: 1 | -1,
  isPinnedFirst: (item: ItemWithSchedule) => boolean = () => false,
): ItemWithSchedule[] => {
  const plannedField = PLANNED_DATE_FIELD_BY_DATE_FIELD[dateField];

  return [...items].sort((a, b) => {
    const aPinned = isPinnedFirst(a);
    const bPinned = isPinnedFirst(b);
    if (aPinned !== bPinned) return aPinned ? -1 : 1;

    const aDate = a[dateField] ?? a[plannedField];
    const bDate = b[dateField] ?? b[plannedField];
    if (aDate === null && bDate === null) return 0;
    if (aDate === null) return -1;
    if (bDate === null) return 1;
    return aDate < bDate ? -sortDirection : aDate > bDate ? sortDirection : 0;
  });
};

export const dependencyPairFor = (
  section: DependencySectionValue,
  currentItemId: number,
  otherItemId: number,
): [predecessorId: number, successorId: number] =>
  section === 'predecessors' ? [otherItemId, currentItemId] : [currentItemId, otherItemId];

export const isParentChildRejection = (serverMessage: string | null): boolean =>
  serverMessage?.includes('cannot be in a parent-child relationship') ?? false;

export const isAlreadyInheritedRejection = (serverMessage: string | null): boolean =>
  serverMessage?.includes('already inherited from an ancestor') ?? false;
